<script lang="ts" setup>
import { type ComponentPublicInstance, computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { type RouteLocationRaw } from 'vue-router';
import { ArrowRightIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import { getExplore, type GetExploreCityResponse, type GetExploreRegionResponse, type GetExploreResponse, type RegionId } from '@/api';
import SectionHeader from '@/components/SectionHeader.vue';
import { useUserStore } from '@/plugins/pinia/userStore.ts';
import { loadPresentationConfiguration } from '@/utilities/presentation-configuration.ts';
import { useSsgState } from '@/utilities/ssg-state.ts';

const { t, locale } = useI18n();

const RegionSearchDistance = 100;

const userStore = useUserStore();

const defaultVisibleCityCount = ref(0);

const explore = ref<GetExploreResponse | null>(null);
const selectedRegionId = ref<RegionId | null>(null);
const expandedRegionId = ref<RegionId | null>(null);

const fetchExplore = async (): Promise<GetExploreResponse> => {
  const fetch = await getExplore();
  if (fetch.response?.ok && fetch.data) {
    return fetch.data;
  } else {
    throw Error(`Fetching explore sections failed: ${fetch.error}`);
  }
};

const loadExplore = async () => {
  try {
    const [data, presentation] = await Promise.all([fetchExplore(), loadPresentationConfiguration()]);
    defaultVisibleCityCount.value = presentation.home.popularCitiesInitialLimit;
    explore.value = {
      regions: (data.regions ?? []).map((region) => ({ ...region, cities: region.cities ?? [] })),
    };
  } catch (err) {
    console.error(err);
  }
};

const { ready } = useSsgState({
  key: 'explore',
  refs: { explore, defaultVisibleCityCount },
  isLoaded: () => explore.value !== null,
  load: loadExplore,
});

onMounted(async () => {
  await ready();
  applyStoredCoordinates();
  window.addEventListener('resize', debouncedCityRowMeasure);
  scheduleCityRowMeasure();
});

const isOtherRegion = (region: GetExploreRegionResponse) => region.name.trim() === '-' || region.name.trim().length === 0;

const regionName = (region: GetExploreRegionResponse) => (isOtherRegion(region) ? t('views.home.explore.regions.other') : region.name);

const regions = computed(() => {
  const list = [...(explore.value?.regions ?? [])];
  return list.sort((a, b) => {
    if (isOtherRegion(a) !== isOtherRegion(b)) {
      return isOtherRegion(a) ? 1 : -1;
    }
    return a.name.localeCompare(b.name, locale.value);
  });
});

// L'API livre les villes triées par nombre de projets décroissant. La rangée en montre
// autant que la largeur mesurée le permet, sans jamais dépasser une seule ligne.
const visibleCityCounts = ref<Record<RegionId, number>>({});
const cityRowElements = new Map<RegionId, HTMLElement>();
const measureRowElements = new Map<RegionId, HTMLElement>();

const setElement = (elements: Map<RegionId, HTMLElement>, regionId: RegionId, element: Element | ComponentPublicInstance | null) => {
  if (element instanceof HTMLElement) {
    elements.set(regionId, element);
  } else {
    elements.delete(regionId);
  }
};

const visibleCityCount = (region: GetExploreRegionResponse) => visibleCityCounts.value[region.id] ?? Math.min(defaultVisibleCityCount.value, region.cities.length);

const measureCityRow = () => {
  const regionId = selectedRegionId.value;
  if (regionId === null) {
    return;
  }

  const row = cityRowElements.get(regionId);
  const measureRow = measureRowElements.get(regionId);
  if (!row || !measureRow || row.clientWidth === 0) {
    return;
  }

  const children = Array.from(measureRow.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
  if (children.length === 0) {
    return;
  }

  const gap = 8;
  const available = row.clientWidth;
  let used = 0;
  let count = 0;
  for (const pill of children) {
    const width = (count > 0 ? gap : 0) + pill.offsetWidth;
    if (used + width > available) {
      break;
    }
    used += width;
    count += 1;
  }

  visibleCityCounts.value = { ...visibleCityCounts.value, [regionId]: count };
};

const scheduleCityRowMeasure = () => void nextTick(measureCityRow);
const debouncedCityRowMeasure = useDebounceFn(measureCityRow, 100);

watch(selectedRegionId, scheduleCityRowMeasure);

onUnmounted(() => {
  window.removeEventListener('resize', debouncedCityRowMeasure);
});

const toggleExpanded = (regionId: RegionId) => {
  expandedRegionId.value = expandedRegionId.value === regionId ? null : regionId;
};

// Présélectionne la région du visiteur à partir des coordonnées déjà consenties et
// conservées dans le magasin persistant : le calcul reste entièrement dans le navigateur.
const applyStoredCoordinates = () => {
  const coordinates = userStore.coordinates;
  if (!coordinates) {
    return;
  }

  let bestRegion: GetExploreRegionResponse | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const region of regions.value) {
    for (const city of region.cities) {
      if (!city.location) {
        continue;
      }
      const deltaLatitude = coordinates.latitude - city.location.latitude;
      const deltaLongitude = (coordinates.longitude - city.location.longitude) * Math.cos((coordinates.latitude * Math.PI) / 180);
      const score = deltaLatitude * deltaLatitude + deltaLongitude * deltaLongitude;
      if (score < bestScore) {
        bestScore = score;
        bestRegion = region;
      }
    }
  }

  if (bestRegion !== null) {
    selectedRegionId.value = bestRegion.id;
  }
};

watch(
  regions,
  (value) => {
    if (selectedRegionId.value === null && value.length > 0) {
      selectedRegionId.value = value.reduce((best, region) => (region.projectCount > best.projectCount ? region : best)).id;
    }
  },
  { immediate: true }
);

const regionRoute = (region: GetExploreRegionResponse): RouteLocationRaw => ({
  name: 'search',
  query: {
    regionId: region.id,
    distance: RegionSearchDistance,
    ...(region.searchLocationId !== null
      ? {
          searchLocationId: region.searchLocationId,
          searchLocationName: region.name,
          searchLocationTypeLabel: region.typeLabel,
          searchLocationKind: 'region',
        }
      : {}),
  },
});

const cityRoute = (city: GetExploreCityResponse): RouteLocationRaw => ({
  name: 'search',
  query: {
    cityId: city.id,
    distance: RegionSearchDistance,
    ...(city.searchLocationId !== null
      ? {
          searchLocationId: city.searchLocationId,
          searchLocationName: city.name,
          searchLocationTypeLabel: city.typeLabel,
          searchLocationKind: 'city',
        }
      : {}),
  },
});
</script>

<template>
  <section v-if="regions.length > 0" class="mb-10">
    <SectionHeader>{{ t('views.home.explore.regions.header') }}</SectionHeader>

    <div :aria-label="t('views.home.explore.regions.header')" class="mb-4 grid grid-cols-2 border-t border-l border-gray-200 sm:grid-cols-3 lg:grid-cols-4" role="tablist">
      <button
        v-for="region in regions"
        :key="region.id"
        :aria-selected="region.id === selectedRegionId"
        :class="[
          'flex cursor-pointer items-center justify-between gap-2 border-r border-b border-gray-200 px-3 py-2 text-left text-sm transition',
          region.id === selectedRegionId ? 'bg-ph-green-700 font-semibold text-white' : 'hover:bg-ph-green-50 bg-white text-gray-700',
        ]"
        role="tab"
        type="button"
        @click="selectedRegionId = region.id"
      >
        <span class="min-w-0 truncate">{{ regionName(region) }}</span>
        <span :class="['text-xs font-semibold whitespace-nowrap tabular-nums', region.id === selectedRegionId ? 'text-ph-green-200' : 'text-ph-green-700']">{{ region.projectCount }}</span>
      </button>
    </div>

    <div v-for="region in regions" :key="region.id" :class="{ hidden: region.id !== selectedRegionId }">
      <div class="relative overflow-hidden">
        <div
          v-if="region.id === selectedRegionId"
          :ref="(element) => setElement(measureRowElements, region.id, element)"
          aria-hidden="true"
          class="pointer-events-none invisible absolute inset-x-0 top-0 flex items-center gap-2 whitespace-nowrap"
        >
          <span v-for="city in region.cities" :key="city.id" class="bg-ph-green-50 border-ph-green-200 text-ph-green-800 inline-flex flex-none items-baseline gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold">
            <span class="max-w-48 truncate">{{ city.name }}</span>
            <span class="text-xs font-medium whitespace-nowrap">({{ city.projectCount }})</span>
          </span>
        </div>
        <div :ref="(element) => setElement(cityRowElements, region.id, element)" :class="expandedRegionId === region.id ? 'flex flex-wrap items-center gap-2' : 'flex items-center gap-2 overflow-hidden whitespace-nowrap'">
          <RouterLink
            v-for="(city, index) in region.cities"
            v-show="expandedRegionId === region.id || index < visibleCityCount(region)"
            :key="city.id"
            :to="cityRoute(city)"
            class="bg-ph-green-50 border-ph-green-200 text-ph-green-800 hover:border-ph-green-600 inline-flex flex-none items-baseline gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold transition"
          >
            <span class="max-w-48 truncate">{{ city.name }}</span>
            <span class="text-ph-green-700 text-xs font-medium whitespace-nowrap">({{ city.projectCount }})</span>
          </RouterLink>
        </div>
      </div>
      <button
        v-show="region.cities.length > visibleCityCount(region)"
        :aria-expanded="expandedRegionId === region.id"
        class="text-ph-green-700 hover:text-ph-green-600 mt-3 inline-flex cursor-pointer items-center gap-1 text-sm font-semibold underline underline-offset-4 transition"
        type="button"
        @click="toggleExpanded(region.id)"
      >
        {{ expandedRegionId === region.id ? t('views.home.explore.regions.show-fewer-cities') : t('views.home.explore.regions.show-cities', { count: region.cities.length }) }}
        <ChevronDownIcon aria-hidden="true" :class="['size-4 transition-transform', expandedRegionId === region.id ? 'rotate-180' : '']" />
      </button>
      <p class="mt-5 flex justify-center">
        <RouterLink :to="regionRoute(region)" class="bg-ph-green-700 hover:bg-ph-green-600 inline-flex items-center gap-2 rounded-lg px-6 py-2.5 font-semibold text-white shadow-sm transition">
          {{ t('views.home.explore.regions.view-all', { count: region.projectCount, name: regionName(region) }, region.projectCount) }}
          <ArrowRightIcon aria-hidden="true" class="size-4" />
        </RouterLink>
      </p>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
